import React, { FC, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { MyAccountContainer } from '@components/organisms/MyAccountContainer';
import { Button, ButtonMode } from '@components/atoms/Button';
import { Input, InputStates, InputTypes, InputVariants } from '@components/atoms/Input';
import { Form } from '@components/atoms/Form';
import { Space } from '@components/atoms/Space';
import { useGetMyOrderDetail } from '@services/useGetMyOrderDetail';
import { useData } from '@hooks/useData';
import { useNotifications } from '@hooks/useNotifications';
import { Select, SelectType } from '@components/atoms/Select';
import { Typography } from '@components/atoms/Typography';
import { useCreateComplaint } from '@services/useCreateComplaint';
import { ErrorMessages } from '@components/atoms/ErrorMessages';
import { InputErrors } from '@types';
import { useGetComplaintCategories } from '@services/useGetComplaintCategories';
import { useGetProductDetail } from '@services/useGetProductDetail';

interface ComplaintFormErrors {
    general: string;
    product_uuid: InputErrors;
    category_uuid: InputErrors;
    comment: InputErrors;
}

export const ComplaintForm: FC = () => {
    const { uuid } = useParams();
    const { showDefaultNotification } = useNotifications();
    const { myOrderDetail, login, complaintCategories, productDetail } = useData();
    const { request: requestMyOrderDetail } = useGetMyOrderDetail();
    const { request: requestComplaintCategories, loading: isLoadingComplaintCategories } = useGetComplaintCategories();
    const { request: requestProductDetail } = useGetProductDetail();
    const {
        request: requestRegisterComplaint,
        success: registerComplaintSuccess,
        errorMessage: registerComplaintErrors,
        validationErrors: registerComplaintValidationErrors,
        lastResponseTime: registerComplaintLastResponseTime,
    } = useCreateComplaint();
    const { t } = useTranslation();

    const formRef = useRef<HTMLFormElement>(null);
    const [store, setStore] = useState<string>('');
    const [selectedProductUuid, setSelectedProductUuid] = useState<string>('');
    const [category, setCategory] = useState<string>('');
    const [categoryOptions, setCategoryOptions] = useState<{ value: string; text: string }[]>([]);
    const [comment, setComment] = useState<string>('');

    const [errors, setErrors] = useState<ComplaintFormErrors>({
        general: '',
        product_uuid: [],
        category_uuid: [],
        comment: [],
    });

    const translations = {
        title: t('complaint-form.title'),
        storeLabel: t('complaint-form.store.label'),
        productLabel: t('complaint-form.product.label'),
        categoryLabel: t('complaint-form.category.label'),
        commentLabel: t('complaint-form.comment.label'),
        submitButton: t('complaint-form.submit-button'),
        submitSuccess: t('complaint-form.submit-success'),
        submitError: t('complaint-form.submit-error'),
        selectProductPlaceholder: t('complaint-form.product.placeholder'),
        selectCategoryPlaceholder: t('complaint-form.category.placeholder'),
        productRequiredError: t('complaint-form.product.required-error'),
        commentRequiredError: t('complaint-form.comment.required-error'),
        categoryRequiredError: t('complaint-form.category.required-error'),
    };

    useEffect(() => {
        requestMyOrderDetail({ uuid: uuid || '' });
        requestComplaintCategories();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (complaintCategories?.data) {
            const options = complaintCategories.data.map((item) => ({
                value: item.uuid || '',
                text: item.name || '',
            }));
            setCategoryOptions([{ value: '', text: translations.selectCategoryPlaceholder || '' }, ...options]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [complaintCategories]);
    useEffect(() => {
        if (myOrderDetail?.data) {
            setStore('');
            setSelectedProductUuid('');
            setCategory('');
        }
    }, [myOrderDetail]);

    useEffect(() => {
        if (productDetail?.data?.merchant?.name) {
            setStore(productDetail.data.merchant.name);
        } else {
            setStore('');
        }
    }, [productDetail]);

    const productOptions = [
        { value: '', text: translations.selectProductPlaceholder },
        ...(myOrderDetail?.data?.products?.map((productItem) => ({
            value: productItem.uuid || '',
            text: productItem.name || '',
        })) || []),
    ];

    useEffect(() => {
        if (registerComplaintLastResponseTime) {
            if (registerComplaintErrors || registerComplaintValidationErrors) {
                const newErrors: ComplaintFormErrors = {
                    general: registerComplaintErrors as string,
                    product_uuid: [],
                    category_uuid: [],
                    comment: [],
                };

                if (registerComplaintValidationErrors) {
                    if (registerComplaintValidationErrors.product_uuid) {
                        newErrors.product_uuid = newErrors.product_uuid.concat(registerComplaintValidationErrors.product_uuid);
                    }
                    if (registerComplaintValidationErrors.category_uuid) {
                        newErrors.category_uuid = newErrors.category_uuid.concat(registerComplaintValidationErrors.category_uuid);
                    }
                    if (registerComplaintValidationErrors.comment) {
                        newErrors.comment = newErrors.comment.concat(registerComplaintValidationErrors.comment);
                    }
                }
                setErrors(newErrors);
            } else if (login) {
                showDefaultNotification({ type: 'success', message: registerComplaintSuccess || translations.submitSuccess });

                setComment('');
                setSelectedProductUuid('');
                setCategory('');

                setErrors({
                    general: '',
                    product_uuid: [],
                    category_uuid: [],
                    comment: [],
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [registerComplaintLastResponseTime]);

    const handleProductChange = (value: string) => {
        requestProductDetail({ uuid: value });
        setSelectedProductUuid(value);
        if (value) {
            requestProductDetail({ uuid: value });
        } else {
            setStore('');
        }
        setErrors((prevErrors) => ({ ...prevErrors, product_uuid: [] }));
    };

    const handleCategoryChange = (value: string) => {
        setCategory(value);
        setErrors((prevErrors) => ({ ...prevErrors, category_uuid: [] }));
    };

    const handleCommentChange = (value: string) => {
        setComment(value);

        setErrors((prevErrors) => ({ ...prevErrors, comment: [] }));
    };

    const validateForm: () => ComplaintFormErrors = () => {
        const newErrors: ComplaintFormErrors = {
            general: '',
            product_uuid: [],
            category_uuid: [],
            comment: [],
        };

        if (!selectedProductUuid.trim()) {
            newErrors.product_uuid.push(translations.productRequiredError);
        }

        if (!category.trim()) {
            newErrors.category_uuid.push(translations.categoryRequiredError);
        }

        if (!comment.trim()) {
            newErrors.comment.push(translations.commentRequiredError);
        }

        setErrors(newErrors);
        return newErrors;
    };

    const handleOnClickSubmitComplaint = () => {
        const validationResult = validateForm();

        if (
            validationResult.product_uuid.length === 0 &&
            validationResult.category_uuid.length === 0 &&
            validationResult.comment.length === 0 &&
            !validationResult.general
        ) {
            formRef?.current?.requestSubmit();
        } else {
            if (validationResult.general === '' && errors.general !== '') {
                setErrors((prev) => ({ ...prev, general: '' }));
            }
        }
    };

    const handleSubmitComplaint = () => {
        const complaintData = {
            category_uuid: category,
            product_uuid: selectedProductUuid,
            comment: comment,
        };

        requestRegisterComplaint(complaintData);
    };

    return (
        <MyAccountContainer title={translations.title}>
            <Space spaceBreakpoints={{ xs: { space: 50 } }} />
            <div>
                <Form formRef={formRef} onSubmit={handleSubmitComplaint}>
                    <>
                        {errors.general && (
                            <>
                                <ErrorMessages messages={[errors.general]} />
                                <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                            </>
                        )}
                    </>

                    <div className="row">
                        {/* Product */}
                        <div className="col-md-6 mb-3">
                            <Typography color="muted" fontSize="12">
                                {`${translations.productLabel}*`}
                            </Typography>
                            <Select
                                classes="w-100"
                                type={SelectType.variant1}
                                options={productOptions}
                                value={selectedProductUuid || ''}
                                onChange={handleProductChange}
                            />
                            {errors.product_uuid.length > 0 && <ErrorMessages messages={errors.product_uuid} />}
                        </div>
                        {/* Store */}
                        <div className="col-md-6 mb-3 pt-md-2 mt-md-1">
                            <Input
                                type={InputTypes.text}
                                label={translations.storeLabel}
                                variant={InputVariants.labeledInput}
                                value={store}
                                disabled={true}
                                onChange={(value) => setStore(value)}
                            />
                        </div>
                        {/* Category */}
                        <div className="col-md-12 mb-3">
                            <Typography color="muted" fontSize="12">
                                {`${translations.categoryLabel}*`}
                            </Typography>
                            <Select
                                classes="w-100"
                                type={SelectType.variant1}
                                options={categoryOptions}
                                value={category || ''}
                                onChange={handleCategoryChange}
                                disabled={isLoadingComplaintCategories}
                            />
                            {errors.category_uuid.length > 0 && <ErrorMessages messages={errors.category_uuid} />}
                        </div>
                        
                        {/* Comment */}
                        <div className="col-md-12 mb-3">
                            <Input
                                type={InputTypes.textarea}
                                label={`${translations.commentLabel}*`}
                                variant={InputVariants.labeledInput}
                                value={comment}
                                onChange={handleCommentChange}
                                state={errors.comment.length > 0 ? InputStates.error : undefined}
                                errorMessages={errors.comment}
                            />
                        </div>
                    </div>
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                    <Button label={translations.submitButton} mode={ButtonMode.primary} onClick={handleOnClickSubmitComplaint} />
                </Form>
            </div>
        </MyAccountContainer>
    );
};
